import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export interface AccordionRowState<TData = any> {
  isExpanded: boolean;
  isLoading: boolean;
  isClosing: boolean;
  data: TData; // Generic data storage for expanded row content
}

export interface TableAccordionState {
  expandedRows: Record<string | number, AccordionRowState<unknown>>;
}

const initialState: TableAccordionState = {
  expandedRows: {},
};

const tableAccordionSlice = createSlice({
  name: 'tableAccordion',
  initialState,
  reducers: {
    toggleStart: (state, action: PayloadAction<{ rowKey: string | number }>) => {
      const { rowKey } = action.payload;
      const currentState = state.expandedRows[rowKey];

      if (currentState?.isExpanded) {
        // Start closing animation
        state.expandedRows[rowKey] = {
          ...currentState,
          isClosing: true,
          isLoading: false,
        };
      } else {
        // Start opening - set loading state
        state.expandedRows[rowKey] = {
          isExpanded: true,
          isLoading: true,
          isClosing: false,
          data: null,
        };
      }
    },

    loadComplete: (
      state,
      action: PayloadAction<{
        rowKey: string | number;
        data: unknown;
      }>
    ) => {
      const { rowKey, data } = action.payload;
      const currentState = state.expandedRows[rowKey];

      if (currentState && currentState.isExpanded) {
        state.expandedRows[rowKey] = {
          ...currentState,
          isLoading: false,
          data,
        };
      }
    },

    closeComplete: (state, action: PayloadAction<{ rowKey: string | number }>) => {
      const { rowKey } = action.payload;
      delete state.expandedRows[rowKey];
    },

    cleanupOffscreen: (state, action: PayloadAction<{ rowKey: string | number }>) => {
      const { rowKey } = action.payload;
      delete state.expandedRows[rowKey];
    },

    resetAll: (state) => {
      state.expandedRows = {};
    },
  },
});

export const { toggleStart, loadComplete, closeComplete, cleanupOffscreen, resetAll } = tableAccordionSlice.actions;

// Selectors
const DEFAULT_ROW_STATE: AccordionRowState<unknown> = {
  isExpanded: false,
  isLoading: false,
  isClosing: false,
  data: null,
};

export const selectAccordionRowState = <TData = unknown>(state: unknown, rowKey: string | number): AccordionRowState<TData> => {
  const typedState = state as { tableAccordion: TableAccordionState };
  return (typedState.tableAccordion.expandedRows[rowKey] as AccordionRowState<TData>) || (DEFAULT_ROW_STATE as AccordionRowState<TData>);
};

export const selectAllExpandedRows = (state: unknown): Record<string | number, AccordionRowState<unknown>> => {
  const typedState = state as { tableAccordion: TableAccordionState };
  return typedState.tableAccordion.expandedRows;
};

export const selectIsRowExpanded = (state: unknown, rowKey: string | number): boolean => {
  const typedState = state as { tableAccordion: TableAccordionState };
  return typedState.tableAccordion.expandedRows[rowKey]?.isExpanded || false;
};

export const selectIsRowLoading = (state: unknown, rowKey: string | number): boolean => {
  const typedState = state as { tableAccordion: TableAccordionState };
  return typedState.tableAccordion.expandedRows[rowKey]?.isLoading || false;
};

export default tableAccordionSlice.reducer;
